export type FetchFlatFileAccessTokenDataType = {
  spaceId: string;
  accessToken: string;
};

export type FetchClientSettingsDataType = {
  stripeUserId: string | null;
};

export type EmptyRequestType = Record<string, unknown>;

export type AblyMessageDataType = { migrating: boolean; total: number };

export const MatchState = {
  UNMATCHED: "unmatched",
  PENDING: "pending",
  CONFIRMED: "confirmed",
  REJECTED: "rejected",
} as const;

export type MatchStateType = (typeof MatchState)[keyof typeof MatchState];

export type MemberDataType = {
  addressLineOne?: string;
  addressLineTwo?: string;
  billingBrand?: string;
  billingLastFour?: string;
  city?: string;
  collectPayment?: boolean;
  dob?: string;
  email: string;
  error?: string;
  firstName: string;
  gender?: string;
  historicalCheckins?: number;
  id?: number;
  lastName: string;
  matchState?: MatchStateType | null;
  matchAccepted?: boolean;
  matchingFeedback?: string;
  memberSince?: string;
  migrated?: boolean;
  migrating?: boolean;
  needPlan?: boolean;
  parentEmail?: string;
  phoneNumber?: string;
  planBillingType?: string;
  planDiscountUuid?: string;
  planEndDate?: string;
  planRemainingSessions?: number;
  planStartBilling?: string;
  planUuid?: string;
  postalCode?: string;
  stateOrProvince?: string;
  stripeId?: string;
  userUuid?: string;
};

export type MemberPayloadType = {
  addressLineOne?: string;
  addressLineTwo?: string;
  city?: string;
  dob?: string;
  email: string;
  firstName: string;
  gender?: string;
  historicalCheckins?: number;
  lastName: string;
  memberSince?: string;
  phoneNumber?: string;
  postalCode?: string;
  stateOrProvince?: string;
  parentEmail?: string;
};

export type PostMigratingMembersPayloadType = {
  data: MemberDataType[];
  spaceId: string;
};

export type PostAssignPlanType = {
  id: number;
  planUuid: string;
  planStartBilling: string;
  planDiscountUuid: string;
  planBillingType: string;
  needPlan: boolean;
  planRemainingSessions: number | null;
  planEndDate: string | null;
};

export type PlanDiscountDataType = {
  data: {
    name: string;
    uuid: string;
  }[];
};

export const PaymentTypes = {
  STRIPE: "STRIPE",
  CASH: "CASH",
  COMP: "COMP",
} as const;

export const PlanTypes = {
  RECURRING: "recurring",
  NON_RECURRING: "non-recurring",
  PUNCH_CARD: "session-pack",
} as const;

export type AssignPlanFormType = {
  plan: {
    value: string;
    label: string;
    category: string;
    limitCheckins: boolean;
    limitCheckinsQuantity: number;
  };
  discount: {
    value: string;
    label: string;
  };
  planBillingDate: string;
  paymentType: string;
  planEndDate: string;
  planRemainingSessions: string;
  punchcardExpires: boolean;
};

export type PostActivateMembersType = {
  data: { id: number; collectPayment: boolean }[];
  notifyUser: boolean;
};

export type PaymentMatchingFileCreateResponseType = {
  fileId: string;
};

export type PaymentMatchingVectorStoreCreateResponseType = {
  vectorStoreId: string;
};

export type PaymentMatchingVectorStoreStatusResponseType = {
  status: string;
};

export type PaymentMigrationResponseType = {
  message: string;
  allJobs: number;
};

export type PaymentMatchingEventDataType = {
  message: string;
};

export type StripePaymentMethodDataType = {
  id: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  us_bank_account?: {
    bank_name: string;
    last4: string;
  };
};

export type StripePaymentData = Record<
  string,
  {
    customerData: {
      id: string;
      address: string | null;
      email: string | null;
      name: string | null;
      phone: string | null;
    };
    paymentMethods: {
      data: StripePaymentMethodDataType[];
    };
  }
>;

export type StripeBillingDataType = {
  success: boolean;
  data: StripePaymentData | string;
};

export type PostCreateMultipleBillingRecordsType = {
  data: PostCreateSingleBillingRecordType[];
};

export type PostCreateSingleBillingRecordType = {
  userId: number;
  userUuid: string;
  paymentReference: string;
  customerReference: string;
  isPermanent: boolean;
};

export const StepIdType = {
  WHO_TO_ACTIVATE: "who-to-activate",
  SELECT_MEMBERS_TABLE: "select-members-table",
  WHO_TO_PROMPT: "who-to-prompt",
  SELECT_PAYMENT_COLLECTION_TABLE: "select-payment-collection-table",
  REVIEW_AND_ACTIVATE: "review-and-activate",
} as const;

export const PaymentSelection = {
  REVIEW_AND_ACTIVATE_ALL: "review-and-activate-all",
  REVIEW_AND_ACTIVATE_NONE: "review-and-activate-none",
  SELECT_PAYMENT_COLLECTION_TABLE: "select-payment-collection-table",
} as const;

export type PaymentSelectionType =
  (typeof PaymentSelection)[keyof typeof PaymentSelection];

export type StepType = {
  id: (typeof StepIdType)[keyof typeof StepIdType];
  step: number;
  title: string;
  isMainStep: boolean;
};

export type UploadedUserUpdatePayload = {
  stripeId?: string | null;
  matchState?: MatchStateType;
};

export type UploadedUserUpdateType = {
  stripeId: string;
};
